/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */
import { useAtomValue } from 'jotai';
import callApi from 'lib/callApi';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Input,
  FormFeedback,
  Alert,
  Progress,
  Spinner,
} from 'reactstrap';
import { companyIdAtom } from 'views/state';

/**
 * Props for the CreateActivationKeyModal component
 */
interface CreateActivationKeyModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Function to toggle the modal */
  toggle: () => void;
  /** List of existing key names to validate against */
  existingKeyNames: string[];
  /** Callback function to execute on successful key creation */
  onSuccess?: (responseData: any) => void;
}

/**
 * Option type for the key type select
 */
interface KeyTypeOption {
  value: string;
  label: string;
}

/**
 * Modal component for creating a new Cloud Agent activation key
 *
 * This component provides a reusable modal for creating new activation keys
 * across different parts of the application. It includes validation to prevent
 * duplicate key names (case-insensitive) and supports creating keys for both
 * Servers and Workstations.
 *
 * The modal handles:
 * - Key name validation (required and uniqueness check)
 * - Key type selection (Servers or Workstations)
 * - API integration for key creation
 * - Success/failure notifications
 * - Loading state management
 *
 * @example
 * ```tsx
 * const [isModalOpen, setIsModalOpen] = useState(false);
 * const toggleModal = () => setIsModalOpen(!isModalOpen);
 * const existingKeys = ['Key1', 'Key2'];
 *
 * // In your component
 * <CreateActivationKeyModal
 *   isOpen={isModalOpen}
 *   toggle={toggleModal}
 *   existingKeyNames={existingKeys}
 *   onSuccess={(responseData) => fetchKeys(responseData)}
 * />
 * ```
 */
const MAX_KEYS = 10;

const CreateActivationKeyModal: React.FC<CreateActivationKeyModalProps> = ({
  isOpen,
  toggle,
  existingKeyNames,
  onSuccess,
}) => {
  // Get company ID from atom
  const companyId = useAtomValue(companyIdAtom);

  // State for form fields and validation
  const [keyName, setKeyName] = useState<string>('');
  const [keyType, setKeyType] = useState<KeyTypeOption>({
    value: 'Servers',
    label: 'Servers',
  });
  const [nameError, setNameError] = useState<string | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [isDuplicateName, setIsDuplicateName] = useState<boolean>(false);
  const [maxKeysError, setMaxKeysError] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string>(
    'Creating activation key...',
  );
  const [loadingProgress, setLoadingProgress] = useState<number>(0);

  // Reference to track if component is mounted
  const isMounted = useRef<boolean>(true);

  // Calculate remaining keys
  const usedKeys = existingKeyNames.length;
  const remainingKeys = MAX_KEYS - usedKeys;
  const isKeyLimitReached = usedKeys >= MAX_KEYS;

  // Calculate progress percentage
  const progressPercentage = (usedKeys / MAX_KEYS) * 100;
  const progressColor =
    progressPercentage >= 90
      ? 'danger'
      : progressPercentage >= 70
      ? 'warning'
      : 'success';

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setKeyName('');
      setKeyType({ value: 'Servers', label: 'Servers' });
      setNameError(undefined);
      setIsDuplicateName(false);
      setMaxKeysError(false);
      setLoadingProgress(0);
    }
  }, [isOpen]);

  // Set up effect to handle component unmounting
  useEffect(() => {
    // Component is mounted
    isMounted.current = true;

    // Cleanup function to run when component unmounts
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Loading progress simulation
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (loading) {
      // Start with initial progress and message
      setLoadingProgress(5);
      setLoadingMessage('Creating activation key...');

      // Set up interval to update progress and messages
      interval = setInterval(() => {
        setLoadingProgress((prev) => {
          const newProgress = prev + 5;

          // Update loading message based on progress
          if (newProgress === 25) {
            setLoadingMessage('Connecting to API...');
          } else if (newProgress === 50) {
            setLoadingMessage('Generating activation key...');
          } else if (newProgress === 75) {
            setLoadingMessage('Finalizing key creation...');
          } else if (newProgress >= 90) {
            setLoadingMessage('Almost there...');
          }

          // Cap progress at 95% until actual completion
          return newProgress > 95 ? 95 : newProgress;
        });
      }, 1000);
    } else {
      // Reset progress when not loading
      setLoadingProgress(0);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [loading]);

  // Key type options
  const keyTypeOptions: KeyTypeOption[] = [
    { value: 'Servers', label: 'Servers' },
    { value: 'Workstations', label: 'Workstations' },
  ];

  /**
   * Handles form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Reset errors
    setNameError(undefined);
    setIsDuplicateName(false);
    setMaxKeysError(false);

    // Validate key name
    if (!validateKeyName(keyName)) {
      return;
    }

    setLoading(true);

    try {
      // Call API to create activation key using the new endpoint
      const response = await callApi(
        'cloud_agents/activation-keys/create-key/',
        'post',
        {},
        {
          title: keyName.trim(),
          key_type: keyType.value,
          company_id: companyId,
          apply_on_agents: true,
        },
      );

      // Only update state if component is still mounted
      if (isMounted.current) {
        // Show success message and reset form
        showSuccessToast('Activation key created successfully');
        setKeyName('');
        setKeyType({ value: 'Servers', label: 'Servers' });

        // Call onSuccess callback if provided with the response data
        if (onSuccess) {
          onSuccess(response);
        }

        // Close the modal
        toggle();
      }
    } catch (error) {
      // Only update state if component is still mounted
      if (!isMounted.current) return;

      console.error('Error creating activation key:', error);

      // Handle error response
      const errorResponse = error as any;
      const errorData = errorResponse?.data;
      let errorMessage = 'Failed to create activation key. ';
      if (errorData) {
        // Check for maximum limit error
        if (
          errorData.error &&
          typeof errorData.error === 'string' &&
          errorData.error.includes('Maximum limit')
        ) {
          setMaxKeysError(true);
          showFailureToast(errorData.error);
          return;
        }

        // Handle field-specific errors
        if (typeof errorData === 'object') {
          Object.keys(errorData).forEach((key) => {
            if (
              key === 'title' &&
              String(errorData[key]).includes('already exists')
            ) {
              setNameError(String(errorData[key]));
              setIsDuplicateName(true);
            }
            errorMessage += `${key}: ${errorData[key]} `;
          });
        } else {
          errorMessage += String(errorData);
        }
      }

      showFailureToast(errorMessage);
    } finally {
      // Only update state if component is still mounted
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  /**
   * Validates the key name
   */
  const validateKeyName = (name: string): boolean => {
    if (!name || name.trim() === '') {
      setNameError('Key name is required');
      setIsDuplicateName(false);
      return false;
    }

    // Check for duplicate key names (case insensitive)
    const inputLower = name.trim().toLowerCase();
    const currentKeyType = keyType.value.toLowerCase();

    // For each existing key, check if it's a duplicate based on name and type
    const nameExists = existingKeyNames.some((existingKey) => {
      const existingLower = existingKey.toLowerCase();

      // Check if the existing key contains a type indicator
      const existingHasServerType = existingLower.includes('server');
      const existingHasWorkstationType = existingLower.includes('workstation');

      // Extract the base name from the existing key (without type)
      let existingBaseName = existingLower;
      if (existingHasServerType) {
        existingBaseName = existingLower.replace(/\s*[-_]?\s*servers?$/i, '');
      } else if (existingHasWorkstationType) {
        existingBaseName = existingLower.replace(
          /\s*[-_]?\s*workstations?$/i,
          '',
        );
      }

      // Check if base names match (case insensitive)
      const baseNamesMatch =
        existingBaseName.toLowerCase() === inputLower.toLowerCase() ||
        existingBaseName
          .toLowerCase()
          .startsWith(inputLower.toLowerCase() + ' ') ||
        existingBaseName
          .toLowerCase()
          .startsWith(inputLower.toLowerCase() + '-') ||
        existingBaseName
          .toLowerCase()
          .startsWith(inputLower.toLowerCase() + '_');

      if (!baseNamesMatch) {
        return false;
      }

      // If base names match, check if types match
      if (currentKeyType === 'servers' && existingHasServerType) {
        return true;
      }

      if (currentKeyType === 'workstations' && existingHasWorkstationType) {
        return true;
      }

      // If types don't match, not a duplicate
      return false;
    });

    if (nameExists) {
      setNameError('Key name already exists. Please choose a unique name.');
      setIsDuplicateName(true);
      return false;
    }

    // Clear any previous error
    setNameError(undefined);
    setIsDuplicateName(false);
    return true;
  };

  /**
   * Handles key name change
   */
  const handleKeyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyName(e.target.value);
    if (e.target.value.trim()) {
      validateKeyName(e.target.value);
    } else {
      setNameError(undefined);
      setIsDuplicateName(false);
    }
  };

  /**
   * Handles key type change
   */
  const handleKeyTypeChange = (selectedOption: KeyTypeOption | null) => {
    if (selectedOption) {
      // First update the key type state and immediately clear errors
      setKeyType(selectedOption);
      setNameError(undefined);
      setIsDuplicateName(false);

      // Force synchronous validation immediately after state update
      if (keyName.trim()) {
        // Directly check if the key would be a duplicate with the new type
        const inputLower = keyName.trim().toLowerCase();
        const currentKeyType = selectedOption.value.toLowerCase();

        // Perform validation directly here for immediate feedback
        const nameExists = existingKeyNames.some((existingKey) => {
          const existingLower = existingKey.toLowerCase();

          // Check if the existing key contains a type indicator
          const existingHasServerType = existingLower.includes('server');
          const existingHasWorkstationType =
            existingLower.includes('workstation');

          // Extract the base name from the existing key (without type)
          let existingBaseName = existingLower;
          if (existingHasServerType) {
            existingBaseName = existingLower.replace(
              /\s*[-_]?\s*servers?$/i,
              '',
            );
          } else if (existingHasWorkstationType) {
            existingBaseName = existingLower.replace(
              /\s*[-_]?\s*workstations?$/i,
              '',
            );
          }

          // Check if base names match (case insensitive)
          const baseNamesMatch =
            existingBaseName.toLowerCase() === inputLower.toLowerCase() ||
            existingBaseName
              .toLowerCase()
              .startsWith(inputLower.toLowerCase() + ' ') ||
            existingBaseName
              .toLowerCase()
              .startsWith(inputLower.toLowerCase() + '-') ||
            existingBaseName
              .toLowerCase()
              .startsWith(inputLower.toLowerCase() + '_');

          if (!baseNamesMatch) {
            return false;
          }

          // If base names match, check if types match
          if (currentKeyType === 'servers' && existingHasServerType) {
            return true;
          }

          if (currentKeyType === 'workstations' && existingHasWorkstationType) {
            return true;
          }

          // If types don't match, not a duplicate
          return false;
        });

        // Update UI based on validation result
        if (nameExists) {
          setNameError('Key name already exists. Please choose a unique name.');
          setIsDuplicateName(true);
        } else {
          setNameError(undefined);
          setIsDuplicateName(false);
        }
      }
    }
  };

  // Custom toggle function that prevents closing when loading
  const handleToggle = () => {
    if (!loading) {
      toggle();
    }
  };

  // CSS for the loading overlay with pattern
  const overlayStyle = {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    // backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(3px)', // Blurred background effect
    WebkitBackdropFilter: 'blur(3px)', // Safari support
    zIndex: 1050,
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    alignItems: 'center',
    padding: '2rem',
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleToggle}
      backdrop={loading ? 'static' : true}
      keyboard={!loading}
    >
      <div tabIndex={-1} style={{ outline: 'none', position: 'relative' }}>
        {/* Loading Overlay */}
        {loading && (
          <div style={overlayStyle}>
            <Spinner
              style={{
                width: '3rem',
                height: '3rem',
              }}
              color="primary"
            />
            <div className="text-center mb-4">
              <p style={{ fontSize: '0.75rem', fontWeight: '600' }}>
                {loadingMessage}
              </p>
              <p style={{ fontSize: '0.65rem' }}>
                This may take up to a minute. Please do not close this window.
              </p>
              <div className="d-flex justify-content-center">
                <Progress
                  value={loadingProgress}
                  className="mt-3"
                  style={{
                    height: '8px',
                    width: '100%',
                    maxWidth: '300px',
                  }}
                />
              </div>
            </div>
          </div>
        )}
        <ModalHeader toggle={loading ? undefined : handleToggle}>
          Create New Activation Key
        </ModalHeader>
        <ModalBody>
          {/* Key Usage Status */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <p className="mb-0 font-weight-bold">Key Usage</p>
              <p>
                {usedKeys}/{MAX_KEYS} keys used
              </p>
            </div>
            <Progress
              value={progressPercentage}
              color={progressColor}
              className="mb-2"
              style={{ height: '8px' }}
            />
            {isKeyLimitReached && (
              <div
                style={{
                  backgroundColor: 'rgba(220, 53, 69, 0.15)', // Light red background
                  border: '1px solid rgba(220, 53, 69, 0.6)', // Darker red border
                  borderRadius: '5px',
                  padding: '12px',
                  color: 'inherit', // Auto-adjusts text color based on theme
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: '8px',
                }}
              >
                <i
                  className="fas fa-exclamation-circle"
                  style={{
                    marginRight: '8px',
                    color: 'rgba(220, 53, 69, 0.9)',
                  }} // Icon color adapts
                ></i>
                <p>
                  Maximum key limit reached. Please contact support at{' '}
                  <a
                    href="mailto:support@securepoint360.com"
                    style={{
                      color: 'inherit',
                      fontWeight: 'bold',
                      textDecoration: 'underline',
                    }}
                  >
                    support@securepoint360.com
                  </a>
                </p>
              </div>
            )}
            {!isKeyLimitReached && remainingKeys <= 2 && (
              <div
                style={{
                  backgroundColor: 'rgba(255, 193, 7, 0.15)', // Subtle yellow background
                  border: '1px solid rgba(255, 193, 7, 0.6)', // Stronger yellow border
                  borderRadius: '5px',
                  padding: '12px',
                  color: 'inherit', // Auto-adjusts for light/dark mode
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: '8px',
                }}
              >
                <i
                  className="fas fa-exclamation-triangle"
                  style={{
                    marginRight: '8px',
                    color: 'rgba(255, 193, 7, 0.9)',
                  }} // Ensures good contrast
                ></i>
                <p>
                  Only {remainingKeys} {remainingKeys === 1 ? 'key' : 'keys'}{' '}
                  remaining.
                </p>
              </div>
            )}
          </div>

          <Form onSubmit={handleSubmit}>
            {maxKeysError && (
              <Alert color="danger">
                Maximum limit of activation keys has been reached. Please
                contact support at{' '}
                <a
                  href="mailto:support@securepoint360.com"
                  style={{
                    color: 'inherit',
                    fontWeight: 'bold',
                    textDecoration: 'underline',
                  }}
                >
                  support@securepoint360.com
                </a>
              </Alert>
            )}
            {/* Key Name Guidelines */}
            <div className="mb-4">
              <p className="font-weight-bold mb-2">Naming Guidelines</p>
              <div
                style={{
                  padding: '12px',
                  backgroundColor: 'rgba(0, 123, 255, 0.15)', // Light blue, works in both themes
                  borderLeft: '4px solid rgba(0, 123, 255, 0.6)', // Blue left border
                  borderRadius: '5px',
                }}
              >
                <ul className="mb-0 pl-3">
                  <li>
                    Use descriptive names that identify the purpose or
                    environment
                  </li>
                  <li>
                    The key type (Server/Workstation) will be added
                    automatically as a suffix
                  </li>
                  <li>Names should be unique for each key type</li>
                </ul>
              </div>
            </div>

            {/* Key Type Selection */}
            <FormGroup>
              <p role="label">Key Type</p>
              <Select
                id="keyType"
                name="keyType"
                value={keyType}
                onChange={handleKeyTypeChange}
                options={keyTypeOptions}
                onBlur={() => validateKeyName(keyName)}
                className="basic-single"
                classNamePrefix="select"
                isSearchable={false}
                isDisabled={loading}
              />
            </FormGroup>

            {/* Key Name Input */}
            <FormGroup>
              <p>Key Name</p>
              <Input
                type="text"
                name="keyName"
                id="keyName"
                placeholder="Enter key name"
                value={keyName}
                maxLength={100}
                onChange={handleKeyNameChange}
                onBlur={() => validateKeyName(keyName)}
                invalid={!!nameError}
                disabled={loading}
              />
              <FormFeedback>{nameError}</FormFeedback>

              {/* Preview of final key name */}
              {keyName && (
                <div className="flex flex-wrap mt-2">
                  <p>
                    Final key name will appear as:{' '}
                    <span className="font-weight-bold">
                      {keyName.trim()} {keyType.value}
                    </span>
                  </p>
                </div>
              )}
            </FormGroup>

            {/* Example Key Names */}
            <div className="mb-3">
              <p className="mb-2 font-weight-bold">Example Key Names</p>
              <div className="d-flex flex-wrap">
                {[
                  'Production',
                  'Development',
                  'QA',
                  'PreProduction',
                  'UAT',
                ].map((example) => (
                  <div
                    key={example}
                    className="badge badge-light mr-2 mb-2 p-2 cursor-pointer"
                    style={{
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.6 : 1,
                      transition: 'all 0.2s ease',
                    }}
                    onClick={() => {
                      if (!loading) {
                        setKeyName(example);
                        validateKeyName(example);
                      }
                    }}
                  >
                    {example}
                  </div>
                ))}
              </div>
            </div>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleToggle} disabled={loading}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSubmit}
            disabled={
              loading || isDuplicateName || !keyName.trim() || isKeyLimitReached
            }
            title={isKeyLimitReached ? 'Maximum key limit reached' : ''}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="mr-2" /> Creating...
              </>
            ) : (
              'Create Key'
            )}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
};

export default CreateActivationKeyModal;
